import { useMemo } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import { useCustomers } from '../api';

interface CustomerSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'> {
  value: string;
  onChange: (customerId: string, label: string) => void;
}

/** Customer picker. `GET /sales/customers` has no server-side fuzzy `q` filter
 * (only `isActive`/`invoicingMode` — see packages/core/src/schemas/sales.ts's
 * `customerListQuerySchema`), so this fetches the active set in full and relies on
 * Mantine's own client-side `searchable` matching — the same "small master, no
 * typeahead needed" precedent as SiteSelect, applied here since a v1 tenant's
 * customer book is expected to be in the tens, not thousands. */
export function CustomerSelect({ value, onChange, ...rest }: CustomerSelectProps) {
  const { data, isLoading } = useCustomers({ isActive: true });

  const options = useMemo(
    () => (data?.items ?? []).map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` })),
    [data],
  );

  return (
    <Select
      {...rest}
      searchable
      data={options}
      value={value || null}
      placeholder={rest.placeholder ?? 'Choose a customer…'}
      onChange={(newValue) => {
        const match = newValue ? options.find((o) => o.value === newValue) : undefined;
        onChange(newValue ?? '', match?.label ?? '');
      }}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No customers found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
